/**
 * Source Viewer Store for the AtomAssist standalone chat Sources tab (#20423).
 *
 * Session-only state driving the in-chat annotated document drawer. Clicking
 * a source in the Sources panel opens the document in a right-side drawer
 * over the conversation (instead of navigating away to the full-page record
 * route, which dropped the annotations carried in React-Router location
 * state) — for either home a source can resolve to, a dataset or a project
 * folder (#2607). Annotations are held here and passed to the viewer as a
 * prop, so the cited (teal) / read (amber) overlay survives.
 */

import { create } from "zustand";

import type { DocumentAnnotation } from "src/views/datasets/documentAnnotations";
import type { AssistantResponseSources } from "../types/provenance";
import type { RecordHome } from "src/api/records";

export interface SourceViewerState {
  /** Whether the in-chat document drawer is open. */
  open: boolean;
  /** Where the open document lives. Null when closed. */
  home: RecordHome | null;
  /** Source data record id of the document to display. */
  sdrId: number | null;
  /** 1-indexed page to open the viewer at (the cited chunk's page). */
  page: number | null;
  /** Chunk id to land on / auto-jump to in the viewer. */
  chunkId: number | string | null;
  /** Cited + read annotations for this document, driving the overlay. */
  annotations: DocumentAnnotation[];
  /**
   * Full message provenance, kept so the viewer can derive cross-reference
   * links for whichever document is open (sources cited for the same claim from
   * other documents) and jump between them without re-fetching (#20423).
   */
  provenance: AssistantResponseSources | null;
}

export interface OpenSourceArgs {
  home: RecordHome;
  sdrId: number;
  page?: number | null;
  chunkId?: number | string | null;
  annotations?: DocumentAnnotation[];
  provenance?: AssistantResponseSources | null;
}

export interface SourceViewerActions {
  openSource: (args: OpenSourceArgs) => void;
  /**
   * Open the source, or close it if that exact source (same sdr + chunk) is
   * already open — the click/unclick toggle from the spec (chat-v2 mockup:
   * openViewer toggles closed on a second click of the same source). Clicking a
   * *different* source switches the viewer to it rather than closing.
   */
  toggleSource: (args: OpenSourceArgs) => void;
  closeSource: () => void;
}

const initialState: SourceViewerState = {
  open: false,
  home: null,
  sdrId: null,
  page: null,
  chunkId: null,
  annotations: [],
  provenance: null,
};

export const useSourceViewerStore = create<
  SourceViewerState & SourceViewerActions
>()((set) => ({
  ...initialState,

  openSource: ({ home, sdrId, page, chunkId, annotations, provenance }) =>
    set({
      open: true,
      home,
      sdrId,
      page: page ?? null,
      chunkId: chunkId ?? null,
      annotations: annotations ?? [],
      provenance: provenance ?? null,
    }),

  toggleSource: ({ home, sdrId, page, chunkId, annotations, provenance }) =>
    set((state) => {
      const sameSource =
        state.open &&
        state.sdrId === sdrId &&
        state.chunkId === (chunkId ?? null);
      if (sameSource) return { ...initialState };
      return {
        open: true,
        home,
        sdrId,
        page: page ?? null,
        chunkId: chunkId ?? null,
        annotations: annotations ?? [],
        provenance: provenance ?? null,
      };
    }),

  closeSource: () => set({ ...initialState }),
}));
